/**
 * Mock data for Finance Module
 * Generates financial transactions from completed missions
 */

import type {
  FinancialTransaction,
  FinancialSummary,
  FinancialFilter,
  PaymentStatus
} from '../../types/finance';
import { generateId } from './mockData';
import { mockMissions } from './mockMission';
import { generateMockMissionDetail } from './mockMissionDetail';



const formatDateTime = (date: Date): string => {
  const dateStr = date.toISOString().split('T')[0];
  const timeStr = date.toTimeString().slice(0, 5);
  return `${dateStr} ${timeStr}`;
};

// Generate transaction ID like FIN-12345
const generateTransactionId = (index: number): string => {
  const num = 10000 + index;
  return `FIN-${num}`;
};

// Generate financial transactions from completed missions
const generateTransactionsFromMissions = (): FinancialTransaction[] => {
  const transactions: FinancialTransaction[] = [];
  let transactionIndex = 0;

  // Get all completed missions
  const completedMissions = mockMissions.filter(m => m.status === 'Completed');

  for (const mission of completedMissions) {
    try {
      // Get mission detail to access pricing information
      const missionDetail = generateMockMissionDetail(mission.id);

      if (!missionDetail || !missionDetail.totalPrice) {
        continue;
      }

      const {
        totalPrice = 0,
        assetBookingFee = 0,
        platformFee = 0,
        ghostEarnings = 0,
        id: missionId,
        code: missionCode,
        title: missionTitle,
        missionType,
        assetId,
        assetName,
        ghostId,
        ghostName,
        updatedAt,
      } = missionDetail;

      // Only generate transactions for missions with valid pricing
      if (totalPrice <= 0) continue;

      const transactionDate = updatedAt ? new Date(updatedAt) : new Date();

      // Randomly assign a valid status
      const possibleStatuses: PaymentStatus[] = ['Authorized', 'Settled', 'Failed', 'Refunded', 'Voided', 'Expired'];
      const randomStatus = possibleStatuses[Math.floor(Math.random() * possibleStatuses.length)];

      // 1. Asset Payment (income)
      transactions.push({
        id: generateId(),
        transactionId: generateTransactionId(transactionIndex++),
        missionId,
        missionType,
        type: 'asset_payment',
        status: randomStatus,
        amount: totalPrice,
        assetId,
        assetName,
        date: transactionDate,
        dateText: formatDateTime(transactionDate),
        description: `Mission payment from ${assetName || 'Asset'}`,
      });

      // 2. Booking Fee (income)
      if (assetBookingFee > 0) {
        transactions.push({
          id: generateId(),
          transactionId: generateTransactionId(transactionIndex++),
          missionId,
          missionCode,
          missionTitle,
          missionType,
          type: 'booking_fee',
          status: 'Settled', // Fees are usually settled
          amount: assetBookingFee,
          assetBookingFee,
          assetId,
          assetName,
          date: transactionDate,
          dateText: formatDateTime(transactionDate),
          description: `Booking fee from ${assetName || 'Asset'}`,
        });
      }

      // 3. Platform Fee (income)
      if (platformFee > 0) {
        transactions.push({
          id: generateId(),
          transactionId: generateTransactionId(transactionIndex++),
          missionId,
          missionType,
          type: 'platform_fee',
          status: 'Settled',
          amount: platformFee,
          platformFee,
          ghostId,
          ghostName,
          date: transactionDate,
          dateText: formatDateTime(transactionDate),
          description: `Platform fee from ${ghostName || 'Ghost'}`,
        });
      }

      // 4. Ghost Payout (expense, stored as a negative amount)
      if (ghostEarnings > 0) {
        transactions.push({
          id: generateId(),
          transactionId: generateTransactionId(transactionIndex++),
          missionId,
          missionCode,
          missionTitle,
          missionType,
          type: 'ghost_payout',
          status: randomStatus === 'Failed' ? 'Voided' : 'Settled',
          amount: -ghostEarnings, // Negative values represent expenses
          ghostEarnings,
          ghostId,
          ghostName,
          date: transactionDate,
          dateText: formatDateTime(transactionDate),
          description: `Payout to ${ghostName || 'Ghost'}`,
        });
      }
    } catch (error) {
      // Skip missions that can't generate detail
      continue;
    }
  }

  // Sort by date (newest first)
  return transactions.sort((a, b) => b.date.getTime() - a.date.getTime());
};

// Generate mock financial transactions
export const mockFinancialTransactions: FinancialTransaction[] = generateTransactionsFromMissions();

// Calculate financial summary
export const calculateFinancialSummary = (
  transactions: FinancialTransaction[],
  filter?: FinancialFilter
): FinancialSummary => {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const yearStart = new Date(now.getFullYear(), 0, 1);

  let filteredTransactions = [...transactions];

  // Apply status filter
  if (filter?.status && filter.status !== 'all') {
    const statuses = Array.isArray(filter.status) ? filter.status : [filter.status];
    filteredTransactions = filteredTransactions.filter(t => statuses.includes(t.status));
  }

  // Apply date range filter
  if (filter?.dateRange) {
    filteredTransactions = filteredTransactions.filter(
      t => t.date >= filter.dateRange!.start && t.date <= filter.dateRange!.end
    );
  }

  // Apply search filter
  if (filter?.search) {
    const searchLower = filter.search.toLowerCase();
    filteredTransactions = filteredTransactions.filter(
      t =>
        t.transactionId.toLowerCase().includes(searchLower) ||
        t.missionId?.toLowerCase().includes(searchLower) ||
        t.assetName?.toLowerCase().includes(searchLower) ||
        t.ghostName?.toLowerCase().includes(searchLower) ||
        t.description?.toLowerCase().includes(searchLower)
    );
  }

  const calculatePeriodSummary = (startDate: Date) => {
    const periodTransactions = filteredTransactions.filter(t => t.date >= startDate);

    return {
      revenue: periodTransactions
        .filter(t => t.type === 'asset_payment' && t.amount > 0)
        .reduce((sum, t) => sum + t.amount, 0),
      bookingFees: periodTransactions
        .filter(t => t.type === 'booking_fee' && t.amount > 0)
        .reduce((sum, t) => sum + t.amount, 0),
      platformFees: periodTransactions
        .filter(t => t.type === 'platform_fee' && t.amount > 0)
        .reduce((sum, t) => sum + t.amount, 0),
      ghostPayouts: Math.abs(
        periodTransactions
          .filter(t => t.type === 'ghost_payout' && t.amount < 0)
          .reduce((sum, t) => sum + t.amount, 0)
      ),
    };
  };

  const today = calculatePeriodSummary(todayStart);
  const thisMonth = calculatePeriodSummary(monthStart);
  const thisYear = calculatePeriodSummary(yearStart);

  const totalRevenue = filteredTransactions
    .filter(t => t.type === 'asset_payment' && t.amount > 0)
    .reduce((sum, t) => sum + t.amount, 0);

  const totalBookingFees = filteredTransactions
    .filter(t => t.type === 'booking_fee' && t.amount > 0)
    .reduce((sum, t) => sum + t.amount, 0);

  const totalPlatformFees = filteredTransactions
    .filter(t => t.type === 'platform_fee' && t.amount > 0)
    .reduce((sum, t) => sum + t.amount, 0);

  const totalGhostPayouts = Math.abs(
    filteredTransactions
      .filter(t => t.type === 'ghost_payout' && t.amount < 0)
      .reduce((sum, t) => sum + t.amount, 0)
  );

  return {
    totalRevenue,
    totalBookingFees,
    totalPlatformFees,
    totalGhostPayouts,
    netIncome: totalRevenue - totalGhostPayouts,
    today,
    thisMonth,
    thisYear,
  };
};
